import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { ConfirmationNumberOutlined as ConfirmationNumberOutlinedIcon } from '@mui/icons-material';
import { formatDate } from '@core/utils/formatting';
import type { FieldTicketResponse, TicketStatus } from '@core/types/ticket';

interface TicketInfoCardProps {
  ticket: FieldTicketResponse;
}

function getStatusColor(status: TicketStatus): 'default' | 'success' | 'primary' | 'error' | 'warning' {
  switch (status) {
    case 'Draft':
      return 'default';
    case 'Under Review':
      return 'warning';
    case 'Approved':
      return 'success';
    case 'Rejected':
      return 'error';
    case 'Closed':
      return 'primary';
    default:
      return 'default';
  }
}

export function TicketInfoCard({ ticket }: TicketInfoCardProps) {
  return (
    <Card sx={{ mb: 2 }} id="ticket-info-card">
      <CardContent sx={{ px: 4, py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ConfirmationNumberOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {ticket.name}
            </Typography>
          </Box>
          <Chip
            label={ticket.status}
            color={getStatusColor(ticket.status)}
            size="small"
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <InfoRow label="Ngày báo cáo" value={formatDate(ticket.reported_date)} />
          <InfoRow label="Người tạo" value={ticket.technician_name || '-'} />
          <InfoRow label="Ngày nhận" value={ticket.received_date ? formatDate(ticket.received_date) : '-'} />
        </Box>
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography
        variant="body2"
        sx={{ minWidth: 110, color: 'text.secondary', fontSize: '0.8125rem' }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          textAlign: 'right',
          flex: 1,
          color: 'text.primary',
          fontSize: '0.8125rem',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
